import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import { useAppContext } from 'providers/AppProvider';
import { ProductReviewDetail } from 'api/products/_requests';

echarts.use([PieChart, TooltipComponent, LegendComponent]);

const classifySentiment = (rating: number) => {
  if (rating >= 4) return 'Positive';
  if (rating === 3) return 'Neutral';
  return 'Negative';
};

const EcomReviewSentimentChart = ({
  height,
  width,
  data = [] // ✅ Fallback to empty array
}: {
  height: string;
  width: string;
  data?: ProductReviewDetail[];
}) => {
  const { getThemeColor } = useAppContext();

  const sentimentData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const counts = data.reduce(
      (acc, review) => {
        const sentiment = classifySentiment(review.rating);
        acc[sentiment]++;
        return acc;
      },
      { Positive: 0, Neutral: 0, Negative: 0 }
    );

    return [
      { value: counts.Positive, name: `Positive (${counts.Positive})` },
      { value: counts.Neutral, name: `Neutral (${counts.Neutral})` },
      { value: counts.Negative, name: `Negative (${counts.Negative})` }
    ];
  }, [data]);

  const getOption = () => ({
    color: [
      getThemeColor('success'),
      getThemeColor('warning'),
      getThemeColor('danger')
    ],
    tooltip: {
      trigger: 'item',
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('body-color') },
      borderWidth: 1,
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      textStyle: {
        color: getThemeColor('body-color')
      }
    },
    series: [
      {
        name: 'Sentiment',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'center',
          fontSize: 14,
          fontWeight: 'bold',
          formatter: () => 'Review\nSentiment'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: sentimentData
      }
    ]
  });

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getOption()}
      style={{ height, width }}
    />
  );
};

export default EcomReviewSentimentChart;
